import { memo, useCallback } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MdOutlineCalendarToday } from 'react-icons/md';
import { Input, PhoneInput } from '@ds';
import { AdminFormRow, AdminFormSection, ConditionalRender } from '@ui';
import { EmployeeFormValues } from '@/types';
import { getFullName } from '@/utils/helpers';
import { EmployeeRole } from '@/api/apis/resources';
import { TMDXFile } from '@/api/apis/files';
import { ActiveAccountInput } from '../inputs/ActiveAccountInput';
import { ControlledTextInput } from '../inputs/ControlledTextInput';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

type UserDetailsSectionProps = {
  isReadOnly?: boolean;
  isContractor?: boolean;
};

export const UserDetailsSection = memo(({ isReadOnly, isContractor }: UserDetailsSectionProps) => {
  const { control, setValue } = useFormContext<EmployeeFormValues>();
  const { countryCodes } = useCountriesData();

  const [firstName, lastName, profilePhoto, position, roles] = useWatch({
    control,
    name: ['firstName', 'lastName', 'profilePhoto', 'position', 'roles'],
  });

  const isSpecialist = roles?.includes(EmployeeRole.ClinicalSpecialist);
  const isRetrievalSurgeon =
    roles?.includes(EmployeeRole.RetrievalSurgeon) || roles?.includes(EmployeeRole.ContractorRetrievalSurgeon);

  const onImageChange = useCallback(
    (file: TMDXFile) => {
      setValue('image', file);
    },
    [setValue],
  );

  return (
    <AdminFormSection
      title="User Details"
      withAvatar
      imageUrl={profilePhoto}
      onImageChange={onImageChange}
      type="user"
      name={getFullName(firstName, lastName)}
      isReadOnly={isReadOnly}
    >
      <AdminFormRow title="First Name">
        <ControlledTextInput name="firstName" aria-label="Contact First Name" isReadOnly isDisabled />
      </AdminFormRow>
      <AdminFormRow title="Last Name">
        <ControlledTextInput name="lastName" aria-label="Contact Last Name" isReadOnly isDisabled />
      </AdminFormRow>
      <AdminFormRow title="Position">
        <Input aria-label="position" type="text" isDisabled value={position} />
      </AdminFormRow>
      <ConditionalRender show={isSpecialist || isRetrievalSurgeon}>
        <AdminFormRow title="Date Of Birth">
          <ControlledTextInput name="dateOfBirth" aria-label="Date of birth" isReadOnly isDisabled />
        </AdminFormRow>
      </ConditionalRender>
      <AdminFormRow title="Email">
        <ControlledTextInput name="email" type="email" aria-label="Contact Email" isReadOnly isDisabled />
      </AdminFormRow>
      <AdminFormRow title="Phone" isRequired>
        <Controller
          name="phone"
          control={control}
          render={({ field: { value, ...field }, fieldState: { invalid, error, isTouched } }) => (
            <PhoneInput
              countries={countryCodes}
              disabled={isReadOnly}
              selectAriaLabel="Phone code input"
              inputAriaLabel="Phone number input"
              value={value}
              isInvalid={invalid && isTouched}
              error={error?.message}
              {...field}
            />
          )}
        />
      </AdminFormRow>
      {!isContractor && (
        <AdminFormRow title="Hire Date">
          <ControlledTextInput
            name="hireDate"
            aria-label="Hire Date"
            isReadOnly
            isDisabled
            endContent={<MdOutlineCalendarToday size={20} />}
          />
        </AdminFormRow>
      )}
      <AdminFormRow title="TSA Precheck Number">
        <ControlledTextInput
          name="tsaPrecheckNumber"
          aria-label="TSA Precheck Number"
          isReadOnly={false}
          isDisabled={isReadOnly}
        />
      </AdminFormRow>
      <ActiveAccountInput isReadOnly={isReadOnly} isContractor={isContractor} />
    </AdminFormSection>
  );
});
